// @ts-nocheck — ported from app/web (noUncheckedIndexedAccess)
/**
 * Series primitive rendering strategy drawings (line/box/label) on the
 * candle series. Fed from the accumulator's drawings Map (full-state
 * snapshots keyed by ID; update overwrites, delete removes).
 *
 * Coordinates: Xloc 0 = bar index (resolved via the accumulator's time
 * column, clamped to the loaded range), Xloc 1 = unix ms. Y = price.
 * Drawing happens in media coordinate space via the renderer contract;
 * positions are recomputed each draw so pan/zoom keeps objects anchored.
 */

import type {
	IPrimitivePaneRenderer,
	IPrimitivePaneView,
	ISeriesApi,
	ISeriesPrimitive,
	SeriesAttachedParameter,
	Time
} from 'lightweight-charts';
import type { StreamAccumulator } from './previewStreamAccumulator';

const DEFAULT_LINE_COLOR = '#2962ff';
const DEFAULT_BOX_BG = 'rgba(41, 98, 255, 0.12)';
const DEFAULT_LABEL_COLOR = '#2962ff';

/** Wire RGBA u32 → CSS color string (same mapping as the adapter). */
function rgbaToCss(v: number | undefined, fallback: string): string {
	if (v === undefined || v === 0) return fallback;
	const a = v & 0xff;
	const b = (v >>> 8) & 0xff;
	const g = (v >>> 16) & 0xff;
	const r = (v >>> 24) & 0xff;
	return `rgba(${r},${g},${b},${(a / 255).toFixed(3)})`;
}

export class DrawingsPrimitive implements ISeriesPrimitive<Time> {
	private chart?: SeriesAttachedParameter<Time, any>['chart'];
	private requestUpdate?: () => void;

	// LWC v5 passes {chart, requestUpdate} to attached() — the series is
	// NOT included. The candle series is provided at construction (the
	// adapter knows it) and used for priceToCoordinate.
	// `windowStart` is the first accumulator bar the chart holds (the adapter
	// renders a window of the history) and `windowEnd` one past the last:
	// drawings are clamped to that span.
	constructor(
		private acc: StreamAccumulator,
		private series: ISeriesApi<any>,
		private windowStart: () => number = () => 0,
		private windowEnd: () => number = () => Infinity
	) {}

	attached(param: SeriesAttachedParameter<Time, any>): void {
		this.chart = param.chart;
		this.requestUpdate = param.requestUpdate;
	}

	/** Ask the chart for a repaint (new/updated/deleted drawings). */
	requestRepaint(): void {
		this.requestUpdate?.();
	}

	/** Bar index → chart time (ms). Clamped: drawings may reference
	 * bars outside the loaded range (e.g. future right edge). */
	private timeAtBarIndex(x: number): number | null {
		const acc = this.acc;
		if (acc.nBars === 0) return null;
		const idx = Math.min(Math.max(Math.round(x), 0), acc.nBars - 1);
		return acc.bars.time[idx];
	}

	/** Resolve a drawing x coordinate to chart time (ms). */
	private resolveX(d: any, field: 'x1' | 'x2'): number | null {
		const raw = d[field];
		if (raw === undefined) return null;
		if (d.xloc === 1) return raw; // bar_time: already unix ms
		return this.timeAtBarIndex(raw);
	}

	paneViews(): ISeriesPrimitivePaneView[] {
		return [new DrawingsView(this)];
	}
}

interface ISeriesPrimitivePaneView {
	renderer(): IPrimitivePaneRenderer | null;
}

class DrawingsView implements ISeriesPrimitivePaneView {
	constructor(private prim: DrawingsPrimitive) {}

	renderer(): IPrimitivePaneRenderer {
		return {
			draw: (target: any) => {
				target.useMediaCoordinateSpace(({ context }: { context: CanvasRenderingContext2D }) => {
					primDraw(this.prim, context);
				});
			}
		};
	}
}

/** Draw every materialized drawing in media (pixel) coordinates. */
function primDraw(prim: DrawingsPrimitive, ctx: CanvasRenderingContext2D): void {
	const chart = (prim as any).chart;
	const series = (prim as any).series;
	const acc = (prim as any).acc as StreamAccumulator;
	if (!chart || !series) return;
	const ts = chart.timeScale();
	const timeToX = (tMs: number): number | null => {
		try {
			const c = ts.timeToCoordinate(Math.floor(tMs / 1000) as Time);
			return c === null ? null : c;
		} catch {
			return null;
		}
	};
	const priceToY = (price: number): number | null => {
		try {
			return series.priceToCoordinate(price);
		} catch {
			return null;
		}
	};

	const resolveX = (d: any, field: 'x1' | 'x2'): number | null => {
		const raw = d[field];
		if (raw === undefined) return null;
		if (d.xloc === 1) return raw;
		if (acc.nBars === 0) return null;
		const idx = Math.min(Math.max(Math.round(raw), 0), acc.nBars - 1);
		return acc.bars.time[idx];
	};

	// Only what's on screen is drawn: a long run can carry tens of thousands
	// of drawings, and this runs on every pan frame. Times left of the chart's
	// window are clamped to its first bar (the chart has no coordinate for them).
	if (acc.nBars === 0) return;
	const winStartIdx = Math.min(Math.max((prim as any).windowStart(), 0), acc.nBars - 1);
	const winStartMs = acc.bars.time[winStartIdx];
	const visible = ts.getVisibleRange();
	const visFromMs = visible ? Math.max(Number(visible.from) * 1000, winStartMs) : winStartMs;
	const visToMs = visible ? Number(visible.to) * 1000 : Infinity;
	const winEndIdx = Math.min(Math.max((prim as any).windowEnd() - 1, winStartIdx), acc.nBars - 1);
	const winEndMs = acc.bars.time[winEndIdx];
	const clampX = (tMs: number): number => (tMs < winStartMs ? winStartMs : tMs > winEndMs ? winEndMs : tMs);

	// Live drawings resolve their span every frame (updates move them);
	// archived ones never change, so theirs is resolved once and cached.
	const span = (d: any): [number, number] | null => {
		const a = resolveX(d, 'x1');
		if (a === null) return null;
		const b = d.objKind === 2 ? a : resolveX(d, 'x2');
		if (b === null) return null;
		return a <= b ? [a, b] : [b, a];
	};
	const archived = acc.archivedDrawings;
	for (let i = 0; i < archived.length; i++) {
		const d = archived[i];
		if (d._t0 === undefined) {
			const s = span(d);
			d._t0 = s ? s[0] : NaN;
			d._t1 = s ? s[1] : NaN;
		}
		if (!(d._t1 >= visFromMs && d._t0 <= visToMs)) continue;
		drawOne(d);
	}
	for (const d of acc.drawings.values()) {
		const s = span(d);
		if (!s || s[1] < visFromMs || s[0] > visToMs) continue;
		drawOne(d);
	}

	function drawOne(d: any): void {
		switch (d.objKind) {
			case 1: {
				// Box: left/top → right/bottom.
				if (d.y1 === undefined || d.y2 === undefined) return;
				const tx1 = resolveX(d, 'x1');
				const tx2 = resolveX(d, 'x2');
				if (tx1 === null || tx2 === null) return;
				const px1 = timeToX(clampX(tx1));
				const px2 = timeToX(clampX(tx2));
				const py1 = priceToY(d.y1);
				const py2 = priceToY(d.y2);
				if (px1 === null || px2 === null || py1 === null || py2 === null) return;
				const left = Math.min(px1, px2);
				const right = Math.max(px1, px2);
				const top = Math.min(py1, py2);
				const bottom = Math.max(py1, py2);
				ctx.save();
				ctx.fillStyle = rgbaToCss(d.color2, DEFAULT_BOX_BG);
				ctx.fillRect(left, top, right - left, bottom - top);
				ctx.strokeStyle = rgbaToCss(d.color, DEFAULT_LINE_COLOR);
				ctx.lineWidth = Math.max(1, d.size === undefined || d.size < 0 ? 1 : d.size);
				ctx.strokeRect(left, top, right - left, bottom - top);
				ctx.restore();
				break;
			}
			case 0: {
				// Line: X1,Y1 → X2,Y2.
				if (d.y1 === undefined || d.y2 === undefined) return;
				const tx1 = resolveX(d, 'x1');
				const tx2 = resolveX(d, 'x2');
				if (tx1 === null || tx2 === null) return;
				const px1 = timeToX(clampX(tx1));
				const px2 = timeToX(clampX(tx2));
				const py1 = priceToY(d.y1);
				const py2 = priceToY(d.y2);
				if (px1 === null || px2 === null || py1 === null || py2 === null) return;
				ctx.save();
				ctx.strokeStyle = rgbaToCss(d.color, DEFAULT_LINE_COLOR);
				ctx.lineWidth = Math.max(1, d.size === undefined || d.size < 0 ? 1 : d.size);
				ctx.beginPath();
				ctx.moveTo(px1, py1);
				ctx.lineTo(px2, py2);
				ctx.stroke();
				ctx.restore();
				break;
			}
			case 2: {
				// Label: text at X1,Y1.
				if (d.y1 === undefined) return;
				const tx = resolveX(d, 'x1');
				if (tx === null) return;
				const px = timeToX(clampX(tx));
				const py = priceToY(d.y1);
				if (px === null || py === null) return;
				ctx.save();
				ctx.fillStyle = rgbaToCss(d.color2 ?? d.color, DEFAULT_LABEL_COLOR);
				ctx.font = '11px sans-serif';
				ctx.textAlign = 'left';
				ctx.textBaseline = 'bottom';
				ctx.fillText(d.text ?? '', px, py);
				ctx.restore();
				break;
			}
		}
	}
}
